import React, { useState, startTransition, useCallback, createContext, useLayoutEffect, useRef } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { MdClose, MdAdd, MdRemoveRedEye, MdCalculate } from 'react-icons/md';
import { useSuspenseQuery, gql, useMutation } from '@apollo/client';
import { type CreateAdInput, type UpdateAdInput, AdForm_GetAdQuery, AdForm_GetAdQueryVariables, AdForm_UpdateAdMutation, AdForm_UpdateAdMutationVariables, Media, MediaRole, MediaType } from '../../../codegen/graphql';
import ImageTranslator from '../ImageTranslator/ImageTranslator';
import { useScreenContext, withScreen } from '../Screen';
import { ImagesSection } from './ImagesSection/ImagesSection';
import { PrimaryInfoSection, PRIMARY_INFO_SECTION_FRAGMENT } from './PrimaryInfoSection';
import { VariationsSection, VARIATIONS_SECTION_FRAGMENT } from './VariationsSection';
import { updateMediaContent, dataURLtoFile } from '../../utils/imageApi';
import { SUPPLIERS_AD_FRAGMENT, SuppliersSection } from './SuppliersSection';
import { LISTINGS_SECTION_FRAGMENT, ListingsSection } from './ListingsSection/ListingsSection';
import { useRequestManager } from '../common/RequestManager';
import { FaExternalLinkSquareAlt, FaImage, FaInfo, FaListAlt, FaStore } from 'react-icons/fa';
import { AdPrices } from '../AdPrices';
import { calculatePrices } from '../../../common/pricesCalculation';
import { selectBestSupplierProductVariation } from '../../../common/selectBestSuplierProductVariation';
import { createListingConfigurationWithDefaults, filterVariationsByConfiguration } from '../../../common/listingVariationsConfiguration';
import inheritMarginOrTargetPrice from '../../../common/inheritMarginOrTargetPrice';
import PricesCalculation_Popover from '../common/PricesCalculation_Popover';
import { getTranslation, TranslatedValue, Translations, useTranslationEdits } from '../common/TranslatedValue';
import { SALE_CHANNEL } from '../../../common/salesChannels';

type FormStep = 'suppliers' | 'pictures' | 'primary' | 'variations' | 'listings' | 'prices';


const AdFormContext = createContext<{
  adData: AdForm_GetAdQuery['ad'];
  updateAd: (toUpdate: UpdateAdInput, updateTitle?: string, forceRefetch?: boolean) => Promise<void>;
  refetchAdData: () => Promise<any>;
}>({
  adData: null,
  updateAd: async () => {},
  refetchAdData: async () => {},
});

export function AdForm() {
  const { id, step } = useParams<{ id: string; step: string }>();
  const screenContext = useScreenContext();

  /*
   * Data fetching and updating
   */
  const { trackRequest } = useRequestManager();

  // Get ad data
  const { data: adQueryData, refetch: refetchAdData } = useSuspenseQuery<
    AdForm_GetAdQuery,
    AdForm_GetAdQueryVariables
  >(GET_AD, {
    variables: { id: id! },
    fetchPolicy: 'cache-and-network',
  });
  const adData = adQueryData.ad;



  // Update ad mutation
  const [updateAdHTTP] = useMutation<
    AdForm_UpdateAdMutation,
    AdForm_UpdateAdMutationVariables
  >(
    UPDATE_AD
  );

  const titleTranslations = adData?.title as Translations | null | undefined;
  const titleEdits = useTranslationEdits(titleTranslations);
  const updateAdRef = useRef<(toUpdate: UpdateAdInput) => void>(() => {});

  if (!adData) {
    throw new Error('Listing 404')
  }

  useLayoutEffect(() => {
    screenContext.nav.setTitle(
      <TranslatedValue
        translations={titleEdits.value}
        renderAs="text"
        onEditTranslation={(updates) => {
          updateAdRef.current({ title: titleEdits.edit(updates) });
        }}
      />
    );
    screenContext.nav.setTitleText(getTranslation(titleEdits.value));
    screenContext.nav.setNavSteps({
      navSteps: STEPS,
      defaultNavStep: 'pictures',
      defaultSubStep: null,
    });

    let minPrices: null | ReturnType<typeof calculatePrices> = null;
    let maxPrices: null | ReturnType<typeof calculatePrices> = null;
    for (const externalListing of adData.externalListings) {
      const listingConfiguration = createListingConfigurationWithDefaults(
        externalListing.configuration,
        adData.variationTypes,
        adData.variations,
      );  
      const filteredVariations = filterVariationsByConfiguration(
        adData.variations,
        listingConfiguration,
      );
      for (const variation of filteredVariations) {
        const supplierProductVariation = selectBestSupplierProductVariation(
          listingConfiguration.supplierKey!,
          externalListing.lang,
          variation.supplierProductVariations,
        );
        if (!supplierProductVariation) {
          continue;
        }
        const calculatedCost = supplierProductVariation.calculatedCost.find((cc) => cc.destinationCountry === externalListing.lang);
        if (!calculatedCost || calculatedCost.totalCostExcludingVAT === 0) {
          continue;
        }

        const marginOrTargetPrices = inheritMarginOrTargetPrice({
          listing: externalListing,
          listingConfiguration: listingConfiguration,
          variation: variation,
          ad: adData,
        });

        const prices = calculatePrices({
          marginPercentOnBaseCost: marginOrTargetPrices.selected.marginPercentOnBaseCost,
          targetListingPriceIncludingVat: marginOrTargetPrices.selected.targetListingPriceIncludingVat,
          listingPriceRoundingMethod: marginOrTargetPrices.selected.listingPriceRoundingMethod,
          productCostExcludingVAT: calculatedCost.totalCostExcludingVAT,
          packingCostExcludingVAT: supplierProductVariation.packaging?.cost ?? 0,
          forPlatform: externalListing.shop.shopType as SALE_CHANNEL,
          supplierName: supplierProductVariation.supplierName,
          origin: 'FR',
          destination: externalListing.lang,
          packageWeight: supplierProductVariation.packaging?.weight ?? 0,
          packageSize1: supplierProductVariation.packaging?.size1 ?? 0,
          packageSize2: supplierProductVariation.packaging?.size2 ?? 0,
          packageSize3: supplierProductVariation.packaging?.size3 ?? 0,
        });
        if (
          !minPrices
          || prices.retailPriceIncludingVAT.lt(minPrices.retailPriceIncludingVAT)
        ) {
          minPrices = prices;
        }
        if (
          !maxPrices
          || prices.retailPriceIncludingVAT.gt(maxPrices.retailPriceIncludingVAT)
        ) {
          maxPrices = prices;
        }
      }
    }

    screenContext.nav.setTitleRightSide(
      <div className="flex flex-row gap-2">
        {minPrices && (
          <PricesCalculation_Popover
            prices={minPrices}
            showLabels={false}
            showListingPrice={false}
            placement="below"
            size="small"
          />
        )}
        {maxPrices && (<>
          <p className="text-gray-700 text-base self-center">Min - Max</p>
          <PricesCalculation_Popover
            prices={maxPrices}
            showLabels={false}
            showListingPrice={false}
            placement="below"
            size="small"
          />
        </>)}
      </div>
    )
  }, [adData, step, STEPS, titleEdits.value]);

  // Update ad callback
  const updateAd = useCallback(async (
    toUpdate: Optional<UpdateAdInput, keyof UpdateAdInput>,
    updateTitle?: string,
    forceRefetch?: boolean,
  ) => {
    // Check if there are changes
    let hasChanges = false;
    Object.keys(toUpdate).forEach((key) => {
      if (toUpdate[key as keyof UpdateAdInput] !== adData[key as keyof typeof adData]) {
        hasChanges = true;
      }
    });

    if (!hasChanges) {
      return;
    }

    // Send request
    await trackRequest(
      updateAdHTTP({
        variables: {
          id: id!,
          input: toUpdate,
        },
      }), {
      title: updateTitle || 'Updating Ad',
      isBlocking: false,
    });

    // Refetch ad data
    if (forceRefetch) {
      await refetchAdData();
    }
  }, [adData, id, updateAdHTTP, refetchAdData, trackRequest]);

  updateAdRef.current = updateAd;


  /*
   * Image editor
   */
  // Used to display the image editor
  const [editingMedias, setEditingMedias] = useState<Media[]>([]);

  const handleStartEditing = useCallback((medias: Media[]) => {
    setEditingMedias(medias);
  }, []);

  const handleEditorFinish = useCallback(async (editedImages: Array<{ serverId?: string; dataUrl: string; editorData?: any }>) => {
    try {
      await trackRequest(
        Promise.all(
          editedImages.map(async ({ serverId, dataUrl, editorData }) => {
            if (!serverId) return;
            const file = dataURLtoFile(dataUrl, `edited-${serverId}.png`);
            await updateMediaContent(serverId, file, editorData);
          })
        ), {
          title: 'Updating images',
          isBlocking: false,
        }
      );
      
      setEditingMedias([]);
      startTransition(() => {
        refetchAdData();
      });
    } catch (error) {
      console.error('Error updating images:', error);
    }
  }, [refetchAdData]);

  /*
   * Render image editor if editing medias
   */
  if (editingMedias.length > 0) {
    return (
      <div className="fixed inset-0 overflow-y-auto z-50 bg-white">
        <ImageTranslator
          initialFiles={[]}
          existingImages={editingMedias.map(media => ({
            url: media.originalUrl,
            id: media.id,
            editorData: media.editorData
          }))}
          onFinish={handleEditorFinish}
          onClose={() => setEditingMedias([])}
        />
      </div>
    );
  }

  /*
   * Render the main form
   */
  return (
    <div className="space-y-6 mx-auto pb-4 px-4">
      {screenContext.nav.currentNavStep === 'suppliers' && (
        <SuppliersSection
          adData={adData}
          onUpdate={updateAd}
        />
      )}

      {screenContext.nav.currentNavStep === 'pictures' && (
        <ImagesSection
          adData={adData}
          refetch={refetchAdData}
          onStartEditing={handleStartEditing}
        />
      )}

      {screenContext.nav.currentNavStep === 'primary' && (
        <PrimaryInfoSection
          adData={adData}
          onUpdate={updateAd}
        />
      )}

      {screenContext.nav.currentNavStep === 'variations' && (
        <VariationsSection
          adData={adData}
          onAdDataChange={updateAd}
          refetch={refetchAdData}
          />
        )}

      {screenContext.nav.currentNavStep === 'listings' && (
        <ListingsSection
          adData={adData}
          refetch={refetchAdData}
        />
      )}

      {screenContext.nav.currentNavStep === 'prices' && (
        <AdPrices adData={adData} />
      )}
    </div>
  );
}

export default withScreen(AdForm);


const STEPS = {
  suppliers: {
    label: 'Suppliers',
    icon: <FaStore />,
  },
  pictures: {
    label: 'Pictures',
    icon: <FaImage />,
  },
  primary: {
    label: 'Primary Info',
    icon: <FaInfo />,
  },
  variations: {
    label: 'Variations',
    icon: <FaListAlt />,
  },
  listings: {
    label: 'Listings',
    icon: <FaExternalLinkSquareAlt />,
  },
  prices: {
    label: 'Prices',
    icon: <MdCalculate />,
  },
};

const AD_FORM_FRAGMENT = gql`
  fragment AdForm_ad on Ad {
    id
    marginPercentOnBaseCost
    targetListingPriceIncludingVat
    listingPriceRoundingMethod
    variations {
      id
      marginPercentOnBaseCost
      targetListingPriceIncludingVat
    }
    ...PrimaryInfoSection_ad
    ...VariationsSection_ad
    ...SuppliersAd
    ...ListingsSection_ad
  }
  ${PRIMARY_INFO_SECTION_FRAGMENT}
  ${VARIATIONS_SECTION_FRAGMENT}
  ${SUPPLIERS_AD_FRAGMENT}
  ${LISTINGS_SECTION_FRAGMENT}
`;

const GET_AD = gql`
  query AdForm_GetAd($id: ID!) {
    ad(id: $id) { 
      ...AdForm_ad
    }
  }
  ${AD_FORM_FRAGMENT}
`;

const UPDATE_AD = gql`
  mutation AdForm_UpdateAd($id: ID!, $input: UpdateAdInput!) {
    updateAd(id: $id, input: $input) {
      ...AdForm_ad
    }
  }
  ${AD_FORM_FRAGMENT}
`;